import { useMemo } from 'react';
import { Image, ImageProps } from 'shared/components/image';
import { env } from 'shared/library/env';
import { Coordinate } from 'shared/@types';
import { Link } from 'shared/components/link';
import noImage from 'shared/assets/no-image.png';
import { staticImageUrl } from 'shared/library/static-images';

type MapPositionProps = {
  coordinate: Coordinate | null;
  place?: string;
  displayAsMarker?: boolean;
} & Omit<ImageProps, 'src' | 'alt'>;

export function MapPosition({ coordinate, place, displayAsMarker, ...props }: MapPositionProps) {
  const mapsUrl = useMemo(() => {
    if (!coordinate) return noImage;

    const { latitude, longitude } = coordinate;
    const baseUrl = 'https://api.mapbox.com/styles/v1/mapbox/streets-v12/static';
    const zoom = displayAsMarker ? 13 : 11;
    const size = '640x640@2x';
    const token = env.MAPBOX_API_KEY;
    const url = staticImageUrl('map-marker', 'full', 480);

    const overlay = displayAsMarker
      ? `pin-s+1e100b(${longitude},${latitude})`
      : `url-${encodeURIComponent(url)}(${longitude},${latitude})`;

    return `${baseUrl}/${overlay}/${longitude},${latitude},${zoom}/${size}?access_token=${token}`;
  }, [coordinate, displayAsMarker]);

  const href = place
    ? `https://www.google.com/maps/place/${place}`
    : `https://www.google.com/maps/search/${coordinate?.latitude},${coordinate?.longitude}`;

  return (
    coordinate && (
      <Link href={href} openInNewTab asBlock>
        <Image
          src={mapsUrl}
          alt="Map"
          {...props}
          sizeType={props.sizeType ?? (props.width ? undefined : 'large')}
        />
      </Link>
    )
  );
}
